import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import type { ActivityFeedInformerItemFindInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-item-find.dto';
import type { ActivityFeedInformerQueryInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-query.dto';
import { NotificationKindEnum } from '@zaoblako/shared/modules/notification/enums/notification.kind.enum';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { NotificationService } from '@zaoblako/web/core/services/notification/notification.service';
import { UnreadCountStorageService } from '@zaoblako/web/core/services/notification/unread-count-storage.service';
import { WebsocketNotificationService } from '@zaoblako/web/core/services/notification/websocket-notification.service';
import { NoDataSize } from '@zaoblako/web/shared/components/no-data/constants/no-data-types.enum';
import { NoDataComponent } from '@zaoblako/web/shared/components/no-data/no-data.component';
import { UserAvatarComponent } from '@zaoblako/web/shared/components/user-avatar/user-avatar.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { ConvertNotificationToLinkPipe } from '@zaoblako/web/shared/pipes/convert-notification-to-link.pipe';
import { NotificationTargetStatusEnum } from '@zaoblako/shared/modules/notification/enums/notification-target-status.enum';
import { NotificationActionEnum } from '@zaoblako/shared/modules/notification/enums/notification.action.enum';
import { BadgeModule } from 'primeng/badge';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-lk-eu-notification-list',
  templateUrl: './lk-eu-notification-list.component.html',
  styleUrls: ['./lk-eu-notification-list.component.scss'],
  imports: [
    CommonModule,
    BadgeModule,
    OverlayPanelModule,
    ButtonComponent,
    ProgressSpinnerModule,
    OrderByPipe,
    UserAvatarComponent,
    TagModule,
    SafeHtmlPipe,
    TooltipModule,
    NoDataComponent,
    SpinnerComponent,
    TruncatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuNotificationListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public informerType: NotificationKindEnum;
  @Input() public icon: string;
  @Input() public header: string;

  @Output() public closeSidebar = new EventEmitter<void>();

  public queryParams: ActivityFeedInformerQueryInterface;
  public loading = false;
  public paginationLoading = false;
  public notifications: ActivityFeedInformerItemFindInterface[] = [];
  public countItemsNotification: number;
  public unreadCount = 0;
  public NoDataSize = NoDataSize.M;

  private newWSMessageSubscription: Subscription;
  private wsNotificationReadSubscription: Subscription;
  private unreadCountSubscription: Subscription;
  private readNotificationSubscription: Subscription;

  public get NotificationTargetStatusEnum() {
    return NotificationTargetStatusEnum;
  }

  public constructor(
    private readonly websocketNotificationService: WebsocketNotificationService,
    private readonly unreadCountStorageService: UnreadCountStorageService,
    private readonly notificationService: NotificationService,
    public readonly globalService: GlobalService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.initQueryParams();
    this.newWSMessageSubscription = this.websocketNotificationService.getNewMessage().subscribe({
      next: (socketMessage) => {
        if (socketMessage.message.kind === this.informerType) {
          this.websocketNotificationService.showNotificationPopup(socketMessage.message);
          this.unreadCount++;
          this.cdRef.markForCheck();
        }
      },
      error: (err) => console.error(err),
      complete: () => console.log('complete')
    });

    this.wsNotificationReadSubscription = this.websocketNotificationService.socketNotificationRead$.subscribe(
      (notification) => {
        if (notification.kind === this.informerType) {
          this.unreadCount--;
          this.cdRef.markForCheck();
        }
      }
    );

    this.unreadCountSubscription = this.notificationService.unreadCount$.subscribe((unreadCount) => {
      this.unreadCount = unreadCount?.[this.informerType] ?? 0;
      this.cdRef.markForCheck();
    });

    this.readNotificationSubscription = this.notificationService.eventReadNotification.subscribe((informerType) => {
      if (this.informerType === informerType) {
        this.unreadCount--;
        this.cdRef.markForCheck();
      }
    });

    this.loadNotifications();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const informerTypeChange = changes['informerType'];
    if (!informerTypeChange || informerTypeChange.firstChange || !this.informerType) {
      return;
    }

    this.syncInformerType();
  }

  public ngOnDestroy() {
    this.newWSMessageSubscription?.unsubscribe();
    this.wsNotificationReadSubscription?.unsubscribe();
    this.unreadCountSubscription?.unsubscribe();
    this.readNotificationSubscription?.unsubscribe();
  }

  private initQueryParams(): void {
    this.queryParams = {
      limit: 20,
      offset: 0,
      kinds: [this.informerType]
    };
  }

  /** Смена вкладки в сайдбаре: один список, новый kind — перезагрузка данных. */
  private syncInformerType(): void {
    if (!this.queryParams) {
      this.initQueryParams();
    } else {
      this.queryParams.kinds = [this.informerType];
    }

    const unreadCount = this.notificationService.unreadCount$.value;
    this.unreadCount = unreadCount?.[this.informerType] ?? 0;
    this.loadNotifications();
  }

  public loadNotifications({ concat } = { concat: false }): void {
    if (!concat) {
      this.loading = true;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();
    }

    this.notificationService.getUnreadNotificationCount().subscribe((unreadCount) => {
      this.notificationService.unreadCount$.next(unreadCount);
    });

    this.websocketNotificationService.findList(this.queryParams).subscribe({
      next: (notifications) => {
        this.notifications = concat ? this.notifications.concat(notifications.items) : notifications.items;
        this.countItemsNotification = notifications.count;
        this.loading = false;
        this.paginationLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public read(notification: ActivityFeedInformerItemFindInterface): void {
    this.websocketNotificationService.readNotification({ notificationIds: notification.notificationIds }).subscribe({
      next: () => {
        this.notifications.splice(this.notifications.indexOf(notification), 1);
        this.unreadCount -= notification.notificationIds?.length ?? 0;
        this.unreadCountStorageService.setParam({
          ...this.notificationService.unreadCount$.value,
          [this.informerType]: this.unreadCount
        });
        this.cdRef.markForCheck();
        if (notification.lastNotification.action === NotificationActionEnum.ACCESS_CARD_UPDATED) {
          this.router.navigate(['/lk/external-user/settings']);
          this.closeSidebar.emit();
        }
      }
    });
  }

  public onScrollEnd(target: HTMLElement): void {
    if (target.offsetHeight + Math.ceil(target.scrollTop) >= target.scrollHeight) {
      if (this.notifications.length < this.countItemsNotification && !this.loading && !this.paginationLoading) {
        this.paginationLoading = true;
        this.queryParams.offset += this.queryParams.limit;
        this.loadNotifications({ concat: true });
      }
    }
  }

  public navigateToLink(notification: ActivityFeedInformerItemFindInterface) {
    this.read(notification);
    this.router
      .navigate([new ConvertNotificationToLinkPipe().transform(notification.lastNotification)])
      .catch(console.error);
    this.closeSidebar.emit();
  }
}
